import { HttpClient } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';
import { Instrument, InvestmentStrategyApiEntity, InvestmentStrategyJsonEntity, Strategy } from '../entities/investment-strategy.model';

export interface InvestmentStrategyState {
  loadingNotedStrategy: boolean;
  notedStrategyResponse: InvestmentStrategyApiEntity;
  loadingStrategies: boolean;
  strategies: Strategy[];
  currentStrategy: Strategy;
  savingStrategy: boolean;
  saveStrategySuccess: boolean;
  deleting: boolean;
  deletionSuccess: boolean;
  strategyAcknowledged: boolean;
  acknowledging: boolean;
  error: Error;
}

const initialState: InvestmentStrategyState = {
  loadingNotedStrategy: false,
  notedStrategyResponse: null,
  loadingStrategies: false,
  strategies: [],
  currentStrategy: null,
  savingStrategy: false,
  saveStrategySuccess: false,
  deleting: false,
  deletionSuccess: false,
  strategyAcknowledged: false,
  acknowledging: false,
  error: null,
};

export const InvestmentStrategyStore = signalStore(
  withState(initialState),
  withComputed(({ currentStrategy, strategies, notedStrategyResponse }) => ({
    currentPerformance: computed(() => currentStrategy().perf * 100),
    notedStrategy: computed(() =>
      strategies() && notedStrategyResponse()
        ? strategies().find((strategy) => strategy.technicalName === notedStrategyResponse().strategy)
        : null,
    ),
  })),
  withMethods((store, httpClient = inject(HttpClient)) => ({
    async loadNotedStrategy() {
      patchState(store, { loadingNotedStrategy: true });
      try {
        const notedStrategyResponse = await firstValueFrom(
          httpClient.get<InvestmentStrategyApiEntity>(`${environment.apihost}/api/first-action/strategy`),
        );
        const currentStrategy = notedStrategyResponse
          ? store.strategies()?.find((strategy) => strategy.technicalName === notedStrategyResponse.strategy)
          : null;
        patchState(store, {
          notedStrategyResponse,
          currentStrategy: currentStrategy ?? store.currentStrategy(),
          strategyAcknowledged: notedStrategyResponse?.acknowledged,
        });
      } catch (error) {
        console.error('Error loading noted strategy:', error);
        patchState(store, { error });
      } finally {
        patchState(store, { loadingNotedStrategy: false });
      }
    },
    async loadStrategies() {
      patchState(store, { loadingStrategies: true });

      try {
        const { strategies = [], products = [] } = await firstValueFrom(
          httpClient.get<InvestmentStrategyJsonEntity>(`${environment.assetshost}/assets/data/investment-strategies.json`),
        );

        const populateInstruments = (instruments: Instrument[]) =>
          instruments.map((instrument) => ({
            ...instrument,
            ...products.find((product) => product.isin === instrument.isin),
          }));

        const populatedStrategies = strategies.map((strategy) => ({
          ...strategy,
          populatedInstruments: populateInstruments(strategy.instruments),
        }));

        patchState(store, { strategies: populatedStrategies, currentStrategy: populatedStrategies[1] });
      } finally {
        patchState(store, { loadingStrategies: false });
      }
    },
    setCurrentStrategy(strategy: Strategy) {
      patchState(store, { currentStrategy: strategy });
    },
    async saveStrategy() {
      patchState(store, { savingStrategy: true });
      try {
        const response = await firstValueFrom(
          httpClient.post<InvestmentStrategyApiEntity>(`${environment.apihost}/api/first-action/strategy`, {
            strategy: store.currentStrategy().technicalName,
            compositions: store.currentStrategy().instruments.map(({ isin, weight }) => ({
              isin,
              ratio: weight,
            })),
          } as InvestmentStrategyApiEntity),
        );
        patchState(store, { savingStrategy: false, saveStrategySuccess: !!response });
      } catch (error) {
        console.error('Error saving strategy:', error);
        patchState(store, { error });
      } finally {
        patchState(store, { savingStrategy: false });
      }
    },
    async deleteStrategy() {
      patchState(store, { deleting: true });
      try {
        const response = await firstValueFrom(httpClient.delete(`${environment.apihost}/api/first-action/strategy`));
        patchState(store, { notedStrategyResponse: null, deletionSuccess: !!response });
      } catch (error) {
        console.error('Error deleting strategy:', error);
        patchState(store, { error });
      } finally {
        patchState(store, { deleting: false });
      }
    },
    clear() {
      patchState(store, initialState);
    },
    async ackowledgeStrategy() {
      patchState(store, { acknowledging: true });
      try {
        const response = await firstValueFrom(httpClient.post(`${environment.apihost}/api/first-action/strategy/ack`, {}));
        patchState(store, { strategyAcknowledged: !!response });
      } catch (error) {
        console.error('Error acknowledging strategy:', error);
        patchState(store, { error });
      } finally {
        patchState(store, { acknowledging: false });
      }
    },
  })),
);
